import { useCallback } from "react";
import type { TFunction } from "i18next";
import { EVENT_TYPE_STUDYGROUP_ONLY } from "@/constants";
import {
  PARTICIPATION_STATE_ACCEPTED,
  PARTICIPATION_STATE_NONE,
} from "@/hooks/useRespondToEvent";
import { toast } from "@/hooks/useToast";
import type {
  EventApiResponse,
  RecurrenceScope,
  ReminderData,
} from "@/lib/humhub-api/types";

type Translate = TFunction;

type JoinResult = {
  success: boolean;
  action: string;
  groupName?: string;
  error?: string;
};

type ForceJoinResult = {
  success: boolean;
  action: string;
  groupName?: string;
  error?: string;
};

type UseEventAttendActionParams = {
  event: EventApiResponse;
  isAttending: boolean;
  onDataRefresh?: () => void;
  respond: (
    eventId: number,
    state:
      | typeof PARTICIPATION_STATE_ACCEPTED
      | typeof PARTICIPATION_STATE_NONE,
    scope?: RecurrenceScope,
  ) => Promise<void>;
  t: Translate;
  uniqueReminderData: ReminderData[];
  createReminders: (
    eventId: number,
    reminders: ReminderData[],
    scope?: RecurrenceScope,
  ) => Promise<unknown>;
  deleteReminders: (
    eventId: number,
    scope?: RecurrenceScope,
  ) => Promise<unknown>;
  handleForceJoin: (event: EventApiResponse) => Promise<ForceJoinResult>;
  handleAutoJoin: (event: EventApiResponse) => Promise<JoinResult>;
};

export const useEventAttendAction = ({
  event,
  isAttending,
  onDataRefresh,
  respond,
  t,
  uniqueReminderData,
  createReminders,
  deleteReminders,
  handleForceJoin,
  handleAutoJoin,
}: UseEventAttendActionParams) => {
  const handleAttendClick = useCallback(
    async (scope?: RecurrenceScope) => {
      if (!event || !event.id || !onDataRefresh) return;

      try {
        const newStatus = isAttending
          ? PARTICIPATION_STATE_NONE
          : PARTICIPATION_STATE_ACCEPTED;

        if (newStatus === PARTICIPATION_STATE_ACCEPTED) {
          if (event.visibility === EVENT_TYPE_STUDYGROUP_ONLY) {
            const forceJoinResult = await handleForceJoin(event);
            if (!forceJoinResult.success) {
              toast({
                title: t("common.error"),
                description:
                  forceJoinResult.error ||
                  t("studyGroup.autoJoinFailed", {
                    groupName: forceJoinResult.groupName,
                  }),
                variant: "error",
              });
              return;
            }

            if (forceJoinResult.action === "force_joined") {
              toast({
                title: t("common.success"),
                description: t("studyGroup.joinedGroupWithAccess", {
                  groupName:
                    forceJoinResult.groupName || event.container?.name || "",
                }),
              });
            }
          } else {
            const autoJoinResult = await handleAutoJoin(event);
            if (!autoJoinResult.success) {
              return;
            }
          }
        }

        await respond(event.id, newStatus, scope);

        if (newStatus === PARTICIPATION_STATE_ACCEPTED) {
          if (uniqueReminderData.length > 0) {
            try {
              await createReminders(event.id, uniqueReminderData, scope);
            } catch (error) {
              console.error("Failed to create reminders:", error);
            }
          }
        } else if (uniqueReminderData.length > 0) {
          await deleteReminders(event.id, scope);
        }

        onDataRefresh();
      } catch (error) {
        console.error("Failed to update attendance status:", error);
      }
    },
    [
      createReminders,
      deleteReminders,
      event,
      handleAutoJoin,
      handleForceJoin,
      isAttending,
      onDataRefresh,
      respond,
      t,
      uniqueReminderData,
    ],
  );

  return { handleAttendClick };
};
